import React, { useCallback, useMemo } from "react";
import { partition } from "@common/helpers/collections";
import { DirectorySort } from "@common/constants/counterparty";
import {
    CounterpartyDirection,
    RegisteredCounterpartyIncludeScope,
} from "@common/helpers/counterparties";
import { Counterparty } from "@common/types/counterparty";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { useConstant } from "@hooks/useConstant";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { findToggledValue } from "@components/pageHeader/filters/FilterPickerList/findToggledValue";
import styles from "../CounterpartyFilter/CounterpartyFilter.module.scss";

const EMPTY_VALUE = "__empty__";
const HAS_ASSIGNED_VALUE = "__has_assigned__";

export interface CounterpartyEntityFilterChange {
    selectedIds: string[] | undefined;
    withEmpty: boolean | undefined;
    hasAssigned: boolean | undefined;
}

interface Props {
    selectedIds: string[] | undefined;
    withEmpty: boolean | undefined;
    onChange: (value: CounterpartyEntityFilterChange) => void;
    emptyLabel: string;
    noMatchLabel: string;
    hideEmptyRow?: boolean;
    hasAssigned?: boolean;
    hasAssignedLabel?: string;
    direction?: CounterpartyDirection;
    singleSelect?: boolean;
    /**
     * Restricts the listed counterparties to those set as the customer on at
     * least one invoice, instead of every counterparty used anywhere in the
     * workspace. Used by the Invoices page and AR Aging report filters.
     */
    invoiceCounterpartiesOnly?: boolean;
}

export const CounterpartyEntityFilter: React.FC<Props> = ({
    selectedIds,
    withEmpty,
    onChange,
    emptyLabel,
    noMatchLabel,
    hideEmptyRow,
    hasAssigned,
    hasAssignedLabel,
    direction,
    singleSelect,
    invoiceCounterpartiesOnly,
}) => {
    const counterparties = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        ...(direction ? { direction } : {}),
        ...(invoiceCounterpartiesOnly ? { invoiceCounterpartiesOnly } : {}),
    });

    const selectedAtOpen = useConstant<Array<string | null> | undefined>(() =>
        withEmpty ? [null] : selectedIds,
    );

    const sections = useMemo(() => {
        if (!counterparties.data) {
            return [];
        }
        const fetchedCounterparties: Array<Counterparty | null> =
            counterparties.data.data.map((item) => item.counterparty);
        const rowCounterparties: Array<Counterparty | null> =
            fetchedCounterparties.includes(null)
                ? fetchedCounterparties
                : [...fetchedCounterparties, null];
        const toItem = (
            counterparty: Counterparty | null,
        ): PickerItemData<string> =>
            counterparty === null
                ? { value: EMPTY_VALUE, label: emptyLabel }
                : { value: counterparty.id, label: counterparty.name };

        const [pinnedCounterparties, otherCounterparties] = partition(
            rowCounterparties,
            (counterparty) =>
                selectedAtOpen?.includes(counterparty?.id ?? null),
        );
        const [namedCounterparties, emptyCounterparties] = partition(
            otherCounterparties,
            (counterparty) => counterparty,
        );
        const presenceItems: Array<PickerItemData<string>> = [
            ...(hasAssignedLabel
                ? [{ value: HAS_ASSIGNED_VALUE, label: hasAssignedLabel }]
                : []),
            ...(hideEmptyRow ? [] : emptyCounterparties.map(toItem)),
        ];

        return [
            { key: "pinned", items: pinnedCounterparties.map(toItem) },
            { key: "counterparties", items: namedCounterparties.map(toItem) },
            { key: "presence", items: presenceItems },
        ];
    }, [
        counterparties.data,
        emptyLabel,
        hasAssignedLabel,
        hideEmptyRow,
        selectedAtOpen,
    ]);

    const value = useMemo(
        () => [
            ...(selectedIds ?? []),
            ...(withEmpty ? [EMPTY_VALUE] : []),
            ...(hasAssigned ? [HAS_ASSIGNED_VALUE] : []),
        ],
        [selectedIds, withEmpty, hasAssigned],
    );

    const handleValueChange = useCallback(
        (nextValue: string[]) => {
            const toggledValue = findToggledValue(value, nextValue);
            if (toggledValue === undefined) {
                return;
            }
            if (toggledValue === EMPTY_VALUE) {
                onChange({
                    selectedIds: undefined,
                    withEmpty: withEmpty ? undefined : true,
                    hasAssigned: undefined,
                });
            } else if (toggledValue === HAS_ASSIGNED_VALUE) {
                onChange({
                    selectedIds: undefined,
                    withEmpty: undefined,
                    hasAssigned: hasAssigned ? undefined : true,
                });
            } else if (selectedIds?.includes(toggledValue)) {
                onChange({
                    selectedIds: selectedIds.filter(
                        (id) => id !== toggledValue,
                    ),
                    withEmpty: undefined,
                    hasAssigned: undefined,
                });
            } else {
                onChange({
                    selectedIds: singleSelect
                        ? [toggledValue]
                        : [...(selectedIds ?? []), toggledValue],
                    withEmpty: undefined,
                    hasAssigned: undefined,
                });
            }
        },
        [value, selectedIds, withEmpty, hasAssigned, onChange, singleSelect],
    );

    if (
        counterparties.data &&
        !counterparties.data.data.length &&
        !hasAssignedLabel
    ) {
        return (
            <p className="text-center mt-5 text-prominent">
                No Vendors/Customers created yet
            </p>
        );
    }

    return (
        <div className={styles.filter}>
            <FilterPickerList
                sections={sections}
                value={value}
                onValueChange={handleValueChange}
                emptyLabel={noMatchLabel}
                status={counterparties.isLoading ? "loading" : "idle"}
                selectedCount={selectedIds?.length ?? 0}
                onDeselect={() =>
                    onChange({
                        selectedIds: undefined,
                        withEmpty: undefined,
                        hasAssigned: undefined,
                    })
                }
            />
        </div>
    );
};
